import { GENDERS } from 'components/intake/PatientInfoSection';
import { FormValues } from 'components/intake/types';
import { ActivityInputValues } from 'components/shared/activityInput/useActivityInput';
import { parseDate, simpleDate } from 'lib/date';
import AttrValue, { AttrValueOptions } from 'models/AttrValue';
import Classification, { ClassificationOptions } from 'models/Classification';
import Group, { GroupOptions } from 'models/Group';
import User from 'models/User';
import { UserOptions } from 'models/User';

import { Address } from './schemas/Address';
import Episode from './Episode';
import EpisodeGroup from './EpisodeGroup';
import RehabState, { RehabStateApiName, RehabStateOptions } from './RehabState';

export const PATIENT = 'patient';
export interface PatientOptions {
  id: string;
  name: string;
  dateOfBirth: string | Date | null;
  sex: string;
  owner: Partial<GroupOptions> | null;
  group: Partial<GroupOptions> | null;
  payor: Partial<GroupOptions> | null;
  planTypeClassification: Partial<ClassificationOptions> | null;
  physicianTeam: Partial<AttrValueOptions> | null;
  episodeClassifications: Partial<ClassificationOptions>[];
  episodeId: string | null;
  locationEpisodeId: string;
  admittedOn: null | Date | string;
  externalId: string | null;
  hasIntegrationExternalId: boolean;
  note: Partial<ActivityInputValues> | null;
  needs: string[];
  antHospitalDischarge: null | Date | string;
  currentRehabState: Partial<RehabStateOptions>;
  caseManager: Partial<UserOptions> | null;
  utilizationManager: Partial<UserOptions> | null;
  attrValues: any[] | null;
  transferredFrom: Partial<GroupOptions> | null;
  episodeGroups: Partial<EpisodeGroup>[];
  address: Address;
  phoneNumber: string;
  hasPendingRequest: boolean;
}

function getDefaults(): PatientOptions {
  return {
    id: '',
    name: '',
    dateOfBirth: '',
    sex: '',
    owner: null,
    group: null,
    planTypeClassification: null,
    physicianTeam: null,
    episodeClassifications: [],
    episodeId: '',
    locationEpisodeId: '',
    admittedOn: null,
    externalId: null,
    hasIntegrationExternalId: false,
    note: null,
    needs: [],
    antHospitalDischarge: null,
    payor: null,
    currentRehabState: {},
    caseManager: null,
    utilizationManager: null,
    attrValues: [],
    transferredFrom: null,
    episodeGroups: [],
    address: {
      address1: '',
      address2: '',
      city: '',
      state: '',
      zip: '',
    },
    phoneNumber: '',
    hasPendingRequest: false,
  };
}

/**
 * @class Patient
 * @classdesc Represents a patient in the system
 * @property {string} id - The patient's id
 * @property {string} name - The patient's name
 * @property {Date | null} dateOfBirth - The patient's date of birth
 * @property {string} sex - The patient's sex
 * @property {Group} owner - The patient's owner
 * @property {Group} group - The patient's rehab facility
 * @property {Classification} planTypeClassification - The patient's plan type classification
 * @property {AttrValue | null} physicianTeam - The patient's physician team
 * @property {Classification[]} episodeClassifications - The patient's episode classifications
 * @property {string} episodeId - The patient's episode id
 * @property {string} locationEpisodeId - The patient's location episode id
 * @property {null | Date} admittedOn - The patient's admission date
 * @property {string | null} externalId - The patient's external id
 * @property {Note | null} note - The patient's note
 * @property {string[]} needs - The patient's needs
 * @property {Date | null} antHospitalDischarge - The patient's ant hospital discharge date
 * @property {Group} payor - The patient's payor
 * @property {RehabState} currentRehabState - The patient's current rehab state
 * @property {Group | null} transferredFrom - Where the patient was transferred from
 * @property {EpisodeGroup[]} episodeGroups - The patient's episode groups
 * @param {Partial<PatientOptions>} [options={}]
 * @example const patient = new Patient({ id: '123' });
 *
 */
export default class Patient {
  id: string;
  name: string;
  dateOfBirth: Date | null;
  sex: string;
  owner: Group | null;
  group: Group | null;
  planTypeClassification: Classification | null;
  physicianTeam: AttrValue | null;
  episodeClassifications: Classification[];
  episodeId: string | null;
  locationEpisodeId: string;
  admittedOn: null | Date;
  externalId: string | null;
  hasIntegrationExternalId: boolean;
  note: Partial<ActivityInputValues> | null;
  needs: string[];
  antHospitalDischarge: null | Date;
  payor: Group | null;
  currentRehabState: RehabState | null;
  caseManager: User | null;
  utilizationManager: User | null;
  attrValues: AttrValue[] | null;
  transferredFrom: Group | null;
  episodeGroups: EpisodeGroup[];
  address: Address;
  phoneNumber: string;
  hasPendingRequest: boolean;

  constructor(opts: Partial<PatientOptions> = {}) {
    const options = { ...getDefaults(), ...opts };
    this.id = options.id;
    this.name = options.name;
    this.dateOfBirth = parseDate(options.dateOfBirth) ?? null;
    this.sex = options.sex;
    this.owner = options.owner ? new Group(options.owner) : null;
    this.group = options.group ? new Group(options.group) : null;
    this.physicianTeam = options.physicianTeam ? new AttrValue(options.physicianTeam) : null;
    this.locationEpisodeId = options.locationEpisodeId;
    this.episodeId = options.episodeId;
    this.admittedOn = parseDate(options.admittedOn);
    this.externalId = options.externalId;
    this.hasIntegrationExternalId = options.hasIntegrationExternalId;
    this.note = options.note;
    this.needs = options.needs;
    this.antHospitalDischarge = parseDate(options.antHospitalDischarge);
    this.payor = options.payor ? new Group(options.payor) : null;
    this.episodeClassifications = options.episodeClassifications.map((ec) => new Classification(ec));
    this.planTypeClassification = options.planTypeClassification
      ? new Classification(options.planTypeClassification)
      : null;
    this.currentRehabState = options.currentRehabState ? new RehabState(options.currentRehabState) : null;
    this.caseManager = options.caseManager ? new User(options.caseManager) : null;
    this.utilizationManager = options.utilizationManager ? new User(options.utilizationManager) : null;
    this.attrValues = options.attrValues?.map((attrValue) => new AttrValue(attrValue)) ?? null;
    this.transferredFrom = options.transferredFrom ? new Group(options.transferredFrom) : null;
    this.episodeGroups = options.episodeGroups.map((episodeGroup) => new EpisodeGroup(episodeGroup));
    this.address = options.address;
    this.phoneNumber = options.phoneNumber;
    this.hasPendingRequest = options.hasPendingRequest;
  }

  static get type() {
    return PATIENT;
  }

  static fromEpisodeData(episode: Episode) {
    const patient = episode.patient;
    const group = { ...episode.latestLocationEpisode?.group };

    group.groupType = {
      ...group.groupType,
      displayName: episode.latestLocationEpisode?.group?.groupType?.displayName,
    };

    if (!patient) return new Patient();

    return new Patient({
      ...patient,
      ...episode,
      group: group?.id ? group : null,
      caseManager: episode.latestLocationEpisode?.caseManagerUser || null,
      utilizationManager: episode.latestLocationEpisode?.utilizationManagerUser || null,
      planTypeClassification: episode.latestLocationEpisode?.planTypeClassification || null,
      episodeClassifications: episode.latestLocationEpisode?.episodeClassifications,
    } as unknown as Partial<PatientOptions>);
  }

  static fromFormValues(values: Partial<FormValues>) {
    return new Patient({
      ...values,
      sex: values.sex?.value,
    } as Partial<PatientOptions>);
  }

  get discharged() {
    return this.currentRehabState?.apiName === RehabStateApiName.Discharged;
  }

  intakeFormValues(): Partial<FormValues> {
    return {
      externalId: this.externalId ?? undefined,

      // Patient details
      dateOfBirth: this.dateOfBirth,
      name: this.name,
      sex: GENDERS.find((g) => g.value === this.sex),
      address: this.address,
      phoneNumber: this.phoneNumber,

      // Episode information
      admittedOn: this.admittedOn ? this.admittedOn.toISOString() : undefined,
      antHospitalDischarge: this.antHospitalDischarge ? this.antHospitalDischarge.toISOString() : undefined,
      caseManager: this.caseManager ?? undefined,
      episodeClassifications: this.episodeClassifications,
      owner: this.owner?.id ? this.owner : undefined,
      physicianTeam: this.physicianTeam ?? undefined,
      planTypeClassification: this.planTypeClassification ?? undefined,
      transferredFrom: this.transferredFrom ?? undefined,
      utilizationManager: this.utilizationManager ?? undefined,
      episodeGroups: this.episodeGroups,

      // Post acute information
      group: this.group ?? undefined,
      locationType: this.group
        ? {
            label: this.group?.groupType?.displayName ?? '',
            value: this.group?.groupType?.apiName ?? '',
          }
        : undefined,
    };
  }

  formatNeedsNote() {
    if (!this.externalId) return null; // currently only applies to patients created via Coordinate
    const needsString = this.needs.length ? `Patient Needs:\n${this.needs.join('\n')}\n\n` : '';
    const noteString = this.note?.text ? `Note:\n${this.note.text}\n\n` : '';
    const dischargeString = this.antHospitalDischarge
      ? `Anticipated Hospital Discharge:\n${simpleDate(this.antHospitalDischarge)}`
      : '';

    return needsString + noteString + dischargeString;
  }

  serialize() {
    const note = this.formatNeedsNote();

    const values = {
      admittedOn: this.admittedOn?.toISOString() || null,
      antHospitalDischarge: this.antHospitalDischarge?.toISOString() || null,
      attachments: (this.note?.attachments ?? []).map((attachment) => attachment.serialize()),
      attrValueIds: [] as string[],
      caseManagerId: this.caseManager?.id || null,
      dateOfBirth: this.dateOfBirth?.toISOString(),
      episodeClassificationIds: this.episodeClassifications.map((ec) => ec.id),
      externalId: this.externalId,
      groupIds: [this.owner?.id, ...this.episodeGroups.filter((eg) => !eg.isOwner).map((eg) => eg.groupId)],
      id: this.id,
      locationEpisodeId: this.locationEpisodeId,
      name: this.name,
      note: note?.length ? note : null,
      ownerId: this.owner?.id,
      planTypeClassificationId: this.planTypeClassification?.id || null,
      groupId: this.group?.id,
      sex: this.sex,
      transferredFromId: this.transferredFrom?.id || null,
      utilizationManagerId: this.utilizationManager?.id || null,
      address: this.address,
      phoneNumber: this.phoneNumber,
    };

    const physicianTeamId = this.physicianTeam?.id;

    if (physicianTeamId) {
      values.attrValueIds.push(physicianTeamId);
    }

    return values;
  }
}
